// 261015 P4c-1 (D11) — BỐ CỤC sơ đồ quan hệ của "Tổng quan truyện". THUẦN: không React / antd / DOM (jest chạy được). SVG tự vẽ, KHÔNG thêm thư viện đồ thị.
//
// Nguyên tắc:
//  - TẤT ĐỊNH: cùng đầu vào ⇒ cùng toạ độ (sắp theo phe → hạng → tên → id; không dùng random, không phụ thuộc thứ tự mảng đầu vào).
//  - Mỗi người là MỘT ô cố định (NODE_W × NODE_H); nhãn bị cắt theo ngân sách ký tự (`fitLabel`) nên KHÔNG tràn ô. Mọi ô không chồng nhau (test kiểm với 24 người).
//  - Không chọn ai ⇒ xem toàn cảnh: các phe là các khung, khung chứa nhân vật chính ở GIỮA lưới; trong khung người quan trọng nhất ở giữa.
//    Chọn một người ⇒ chỉ còn người đó + người có quan hệ trực tiếp: người được chọn ở GIỮA, người còn lại xếp vòng elip quanh nó.
//  - Mũi tên CHỈ cho quan hệ có hướng: `parent-child` (cha/mẹ → con) và `master-servant` (chủ → tớ), theo hướng a → b của dữ liệu.
import type { OverviewRelationshipType } from '../../../../types/adaptation';

// ─── Đầu vào chuẩn hoá (tổng quan có trích HOẶC quan hệ ③ cũ đều quy về đây) ───────────────────────────────────────

export interface GraphNode {
  id: string;
  /** Tên đã chọn theo công tắc tên truyện/tên phim. */
  label: string;
  /** id phe (rỗng = chưa thuộc phe nào). */
  group: string;
  /** 0 = nhân vật chính · 1 = phụ · 2 = chức năng · 3 = khác/chưa ánh xạ. Nhỏ ⇒ quan trọng hơn. */
  rank: number;
  /** Đã cắt khỏi phim (nhãn C29) — chỉ để tô nhạt. */
  cut?: boolean;
}
export interface GraphEdge { id: string; a: string; b: string; type: OverviewRelationshipType }
export interface GraphGroup { id: string; label: string }
export interface GraphInput { nodes: GraphNode[]; edges: GraphEdge[]; groups: GraphGroup[] }

export type RelationFilter = 'all' | 'family' | 'power' | 'ally-rival';
export interface LayoutOptions { focusId?: string | null; filter?: RelationFilter; query?: string }

// ─── Đầu ra ───────────────────────────────────────────────────────────────────────────────────────────────

export interface LaidNode {
  id: string; label: string; fullLabel: string; x: number; y: number; w: number; h: number; cx: number; cy: number;
  focus: boolean; matched: boolean; dim: boolean; cut: boolean; group: string;
}
export interface LaidEdge {
  id: string; a: string; b: string; type: OverviewRelationshipType; directed: boolean;
  x1: number; y1: number; x2: number; y2: number; labelX: number; labelY: number; labelText: string; dim: boolean;
}
export interface LaidGroup { id: string; label: string; x: number; y: number; w: number; h: number }
export interface Layout {
  width: number; height: number; mode: 'overview' | 'focus';
  nodes: LaidNode[]; edges: LaidEdge[]; groups: LaidGroup[];
  /** Số người/quan hệ đang vẽ so với tổng (để ghi "Đang hiện 5 / 24 người"). */
  shownNodes: number; totalNodes: number; shownEdges: number; totalEdges: number;
}

// ─── Hằng số hình học ─────────────────────────────────────────────────────────────────────────────────────

export const NODE_W = 156;
export const NODE_H = 44;
const GAP_X = 18;
const GAP_Y = 14;
const BOX_PAD = 16;
const BOX_TITLE = 22;
const BOX_GAP = 26;
const MARGIN = 24;
/** Ngân sách nhãn: chữ Latin = 1 đơn vị, chữ CJK = 2 đơn vị; nhãn dài hơn bị cắt `…`. 17 đơn vị ≈ 125 px ở cỡ chữ 12.5 — vừa trong ô 156 px kèm lề. */
export const LABEL_BUDGET = 17;

export const RELATION_LABEL: Record<OverviewRelationshipType, string> = {
  'parent-child': 'cha/mẹ → con', sibling: 'anh chị em', spouse: 'vợ chồng', kin: 'họ hàng',
  'same-faction': 'cùng phe', 'master-servant': 'chủ → tớ', ally: 'đồng minh', rival: 'đối thủ',
};
/** Loại quan hệ CÓ HƯỚNG ⇒ vẽ mũi tên a → b. Còn lại là hai chiều ⇒ đường thường. */
export const DIRECTED_TYPES: ReadonlyArray<OverviewRelationshipType> = ['parent-child', 'master-servant'];
export const FILTER_TYPES: Record<Exclude<RelationFilter, 'all'>, ReadonlyArray<OverviewRelationshipType>> = {
  family: ['parent-child', 'sibling', 'spouse', 'kin'],
  power: ['same-faction', 'master-servant'],
  'ally-rival': ['ally', 'rival'],
};
export const FILTER_LABEL: Record<RelationFilter, string> = { all: 'Mọi quan hệ', family: 'Gia đình', power: 'Cùng phe / chủ–tớ', 'ally-rival': 'Đồng minh / đối thủ' };

// ─── Tiện ích thuần ───────────────────────────────────────────────────────────────────────────────────────

const isWide = (ch: string): boolean => /[⺀-鿿ꥠ-꥿가-퟿豈-﫿︰-﹏＀-￯]/.test(ch);

/** Độ rộng ước lượng (đơn vị): CJK = 2, còn lại = 1 (jest không có DOM nên không đo chữ thật). */
export const labelUnits = (s: string): number => Array.from(s).reduce((n, ch) => n + (isWide(ch) ? 2 : 1), 0);

/** Cắt nhãn cho vừa ô; không đổi nhãn đã vừa. Luôn trả chuỗi có `labelUnits ≤ budget`. */
export const fitLabel = (s: string, budget: number = LABEL_BUDGET): string => {
  const t = String(s ?? '').replace(/\s+/g, ' ').trim();
  if (labelUnits(t) <= budget) return t;
  let out = '';
  let used = 0;
  for (const ch of Array.from(t)) {
    const u = isWide(ch) ? 2 : 1;
    if (used + u > budget - 1) break;   // chừa 1 đơn vị cho dấu …
    out += ch; used += u;
  }
  return `${out.trimEnd()}…`;
};

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
const norm = (s: string): string => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase().trim();

/** Ô (cột, hàng) của lưới, sắp theo khoảng cách tới tâm lưới (gần tâm trước), hoà ⇒ hàng rồi cột. Dùng để đặt thứ quan trọng nhất ở GIỮA. */
export const centerFirstSlots = (count: number): Array<{ col: number; row: number }> => {
  const cols = Math.max(1, Math.ceil(Math.sqrt(count)));
  const rows = Math.max(1, Math.ceil(count / cols));
  const cx = (cols - 1) / 2;
  const cy = (rows - 1) / 2;
  const slots: Array<{ col: number; row: number; d: number }> = [];
  for (let r = 0; r < rows; r += 1) for (let c = 0; c < cols; c += 1) slots.push({ col: c, row: r, d: (c - cx) ** 2 + (r - cy) ** 2 });
  slots.sort((p, q) => p.d - q.d || p.row - q.row || p.col - q.col);
  return slots.slice(0, count).map(({ col, row }) => ({ col, row }));
};

export const edgeMatchesFilter = (type: OverviewRelationshipType, filter: RelationFilter): boolean => filter === 'all' || FILTER_TYPES[filter].includes(type);

/** Thứ tự ổn định của người: hạng → tên → id. */
const nodeOrder = (a: GraphNode, b: GraphNode): number => a.rank - b.rank || cmp(norm(a.label), norm(b.label)) || cmp(a.id, b.id);

const degreeMap = (edges: ReadonlyArray<GraphEdge>): Map<string, number> => {
  const m = new Map<string, number>();
  for (const e of edges) { m.set(e.a, (m.get(e.a) ?? 0) + 1); m.set(e.b, (m.get(e.b) ?? 0) + 1); }
  return m;
};

/** Người quan trọng nhất = hạng nhỏ nhất, nhiều quan hệ nhất, rồi tên/id. */
const importanceOrder = (deg: Map<string, number>) => (a: GraphNode, b: GraphNode): number =>
  a.rank - b.rank || (deg.get(b.id) ?? 0) - (deg.get(a.id) ?? 0) || nodeOrder(a, b);

const clipToRect = (cx: number, cy: number, tx: number, ty: number, hw: number, hh: number): { x: number; y: number } => {
  const dx = tx - cx;
  const dy = ty - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };
  const sx = dx === 0 ? Infinity : hw / Math.abs(dx);
  const sy = dy === 0 ? Infinity : hh / Math.abs(dy);
  const s = Math.min(sx, sy);
  return { x: cx + dx * s, y: cy + dy * s };
};

const makeNode = (n: GraphNode, x: number, y: number, o: { focusId: string | null; q: string }): LaidNode => {
  const matched = !!o.q && norm(n.label).includes(o.q);
  return {
    id: n.id, label: fitLabel(n.label), fullLabel: n.label, x, y, w: NODE_W, h: NODE_H, cx: x + NODE_W / 2, cy: y + NODE_H / 2,
    focus: o.focusId === n.id, matched, dim: !!o.q && !matched && o.focusId !== n.id, cut: !!n.cut, group: n.group,
  };
};

const buildEdges = (edges: ReadonlyArray<GraphEdge>, pos: Map<string, LaidNode>, showLabel: boolean, dimmedBy: (e: GraphEdge) => boolean): LaidEdge[] => {
  const out: LaidEdge[] = [];
  for (const e of [...edges].sort((p, q) => cmp(p.id, q.id))) {
    const A = pos.get(e.a);
    const B = pos.get(e.b);
    if (!A || !B || A === B) continue;
    const p1 = clipToRect(A.cx, A.cy, B.cx, B.cy, A.w / 2, A.h / 2);
    const p2 = clipToRect(B.cx, B.cy, A.cx, A.cy, B.w / 2, B.h / 2);
    out.push({
      id: e.id, a: e.a, b: e.b, type: e.type, directed: DIRECTED_TYPES.includes(e.type),
      x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, labelX: (p1.x + p2.x) / 2, labelY: (p1.y + p2.y) / 2 - 4,
      labelText: showLabel ? RELATION_LABEL[e.type] : '', dim: dimmedBy(e),
    });
  }
  return out;
};

const overlaps = (a: { x: number; y: number }, b: { x: number; y: number }, gx = GAP_X / 2, gy = GAP_Y / 2): boolean =>
  Math.abs(a.x - b.x) < NODE_W + gx && Math.abs(a.y - b.y) < NODE_H + gy;

// ─── Bố cục ───────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Bố cục sơ đồ. `opts.focusId` hợp lệ ⇒ chế độ `focus` (người đó ở giữa + người có quan hệ trực tiếp, đã qua `filter`); ngược lại `overview`.
 * `opts.filter` lọc LOẠI quan hệ (cả hai chế độ). `opts.query` chỉ TÔ/làm mờ người (không ẩn ai) — tìm tên.
 */
export const layoutRelationGraph = (input: GraphInput, opts: LayoutOptions = {}): Layout => {
  const filter = opts.filter ?? 'all';
  const q = norm(opts.query ?? '');
  const known = new Map(input.nodes.map((n) => [n.id, n] as const));
  // Quan hệ hợp lệ: cả hai đầu có trong danh sách người, khác nhau, đúng bộ lọc loại; bỏ trùng theo id.
  const seen = new Set<string>();
  const edgesAll = input.edges.filter((e) => known.has(e.a) && known.has(e.b) && e.a !== e.b && !seen.has(e.id) && (seen.add(e.id), true));
  const edges = edgesAll.filter((e) => edgeMatchesFilter(e.type, filter));
  const focusId = opts.focusId && known.has(opts.focusId) ? opts.focusId : null;

  return focusId ? layoutFocus(input, known, edges, edgesAll.length, focusId, q) : layoutOverview(input, known, edges, edgesAll.length, q);
};

function layoutOverview(input: GraphInput, known: Map<string, GraphNode>, edges: GraphEdge[], totalEdges: number, q: string): Layout {
  const deg = degreeMap(edges);
  const byImportance = importanceOrder(deg);
  const labelOf = new Map(input.groups.map((g) => [g.id, g.label] as const));
  // Gom người theo phe; phe có người quan trọng nhất đứng đầu (⇒ ở giữa lưới); "chưa thuộc phe" luôn cuối khi hoà.
  const buckets = new Map<string, GraphNode[]>();
  for (const n of input.nodes) {
    const g = n.group && labelOf.has(n.group) ? n.group : '';
    if (!buckets.has(g)) buckets.set(g, []);
    (buckets.get(g) as GraphNode[]).push(n);
  }
  const groupsSorted = Array.from(buckets.entries()).map(([id, list]) => ({ id, list: [...list].sort(byImportance) }))
    .sort((p, q2) => byImportance(p.list[0], q2.list[0]) || (p.id === '' ? 1 : q2.id === '' ? -1 : 0) || cmp(p.id, q2.id));

  // Lưới các khung: ô nào cũng cao/rộng bằng khung lớn nhất cùng hàng/cột.
  const slots = centerFirstSlots(groupsSorted.length);
  const boxes = groupsSorted.map((g, i) => {
    const cols = Math.max(1, Math.ceil(Math.sqrt(g.list.length)));
    const rows = Math.max(1, Math.ceil(g.list.length / cols));
    return { ...g, slot: slots[i], cols, rows, w: BOX_PAD * 2 + cols * NODE_W + (cols - 1) * GAP_X, h: BOX_TITLE + BOX_PAD * 2 + rows * NODE_H + (rows - 1) * GAP_Y };
  });
  const gridCols = Math.max(...boxes.map((b) => b.slot.col), 0) + 1;
  const gridRows = Math.max(...boxes.map((b) => b.slot.row), 0) + 1;
  const colW = Array.from({ length: gridCols }, (_v, c) => Math.max(0, ...boxes.filter((b) => b.slot.col === c).map((b) => b.w)));
  const rowH = Array.from({ length: gridRows }, (_v, r) => Math.max(0, ...boxes.filter((b) => b.slot.row === r).map((b) => b.h)));
  const colX = colW.map((_w, c) => MARGIN + colW.slice(0, c).reduce((s, w) => s + w + BOX_GAP, 0));
  const rowY = rowH.map((_h, r) => MARGIN + rowH.slice(0, r).reduce((s, h) => s + h + BOX_GAP, 0));

  const pos = new Map<string, LaidNode>();
  const groups: LaidGroup[] = [];
  for (const b of boxes) {
    // Ô trong khung cũng xếp từ giữa ra; khung nhỏ hơn ô lưới thì canh giữa theo cột/hàng.
    const gx = colX[b.slot.col] + (colW[b.slot.col] - b.w) / 2;
    const gy = rowY[b.slot.row] + (rowH[b.slot.row] - b.h) / 2;
    groups.push({ id: b.id, label: b.id ? (labelOf.get(b.id) as string) : 'Chưa thuộc phe nào', x: gx, y: gy, w: b.w, h: b.h });
    const inner = centerFirstSlots(b.list.length);
    // `centerFirstSlots` tính lưới theo sqrt(n) — trùng `cols/rows` của khung ở trên.
    b.list.forEach((n, i) => {
      const s = inner[i];
      pos.set(n.id, makeNode(n, gx + BOX_PAD + s.col * (NODE_W + GAP_X), gy + BOX_TITLE + BOX_PAD + s.row * (NODE_H + GAP_Y), { focusId: null, q }));
    });
  }
  const nodes = Array.from(pos.values()).sort((a, b) => cmp(a.id, b.id));
  const width = MARGIN * 2 + colW.reduce((s, w) => s + w, 0) + BOX_GAP * Math.max(0, gridCols - 1);
  const height = MARGIN * 2 + rowH.reduce((s, h) => s + h, 0) + BOX_GAP * Math.max(0, gridRows - 1);
  return {
    width: Math.max(width, 2 * MARGIN + NODE_W), height: Math.max(height, 2 * MARGIN + NODE_H), mode: 'overview', nodes, groups,
    // Toàn cảnh KHÔNG ghi nhãn trên đường (quá dày) — loại quan hệ nằm ở chú giải + tooltip + danh sách bên cạnh.
    edges: buildEdges(edges, pos, false, () => false),
    shownNodes: nodes.length, totalNodes: input.nodes.length, shownEdges: edges.length, totalEdges,
  };
}

function layoutFocus(input: GraphInput, known: Map<string, GraphNode>, edges: GraphEdge[], totalEdges: number, focusId: string, q: string): Layout {
  const incident = edges.filter((e) => e.a === focusId || e.b === focusId);
  const neighborIds = new Set<string>();
  for (const e of incident) neighborIds.add(e.a === focusId ? e.b : e.a);
  const focusNode = known.get(focusId) as GraphNode;
  const neighbors = Array.from(neighborIds).map((id) => known.get(id) as GraphNode).sort(nodeOrder);

  // Vòng elip đồng tâm quanh người được chọn. Mỗi vòng nhồi nhiều nhất có thể mà KHÔNG ô nào chồng ô khác (kiểm thật từng ô).
  const placed: Array<{ n: GraphNode; x: number; y: number }> = [];
  const centerPos = { x: 0, y: 0 };
  let remaining = [...neighbors];
  let ring = 0;
  while (remaining.length) {
    const rx = 230 + ring * (NODE_W + 44);
    const ry = 140 + ring * (NODE_H + 52);
    const phase = ring % 2 === 0 ? -Math.PI / 2 : -Math.PI / 2 + Math.PI / 12;   // vòng sau lệch nhẹ để không thẳng hàng với vòng trước
    let take = 0;
    let candidate: Array<{ n: GraphNode; x: number; y: number }> = [];
    for (let k = 1; k <= remaining.length; k += 1) {
      const trial = remaining.slice(0, k).map((n, i) => {
        const t = phase + (2 * Math.PI * i) / k;
        return { n, x: centerPos.x + rx * Math.cos(t) - NODE_W / 2, y: centerPos.y + ry * Math.sin(t) - NODE_H / 2 };
      });
      const clash = trial.some((p, i) => overlaps(p, { x: centerPos.x - NODE_W / 2, y: centerPos.y - NODE_H / 2 })
        || placed.some((o) => overlaps(p, o)) || trial.slice(i + 1).some((o) => overlaps(p, o)));
      if (clash) break;
      take = k; candidate = trial;
    }
    if (take === 0) { ring += 1; continue; }   // vòng này quá chật cho dù chỉ 1 người ⇒ mở vòng ngoài
    placed.push(...candidate);
    remaining = remaining.slice(take);
    ring += 1;
  }

  const all = [{ n: focusNode, x: centerPos.x - NODE_W / 2, y: centerPos.y - NODE_H / 2 }, ...placed];
  const minX = Math.min(...all.map((p) => p.x));
  const minY = Math.min(...all.map((p) => p.y));
  const maxX = Math.max(...all.map((p) => p.x + NODE_W));
  const maxY = Math.max(...all.map((p) => p.y + NODE_H));
  const ox = MARGIN - minX;
  const oy = MARGIN - minY;
  const pos = new Map<string, LaidNode>();
  for (const p of all) pos.set(p.n.id, makeNode(p.n, p.x + ox, p.y + oy, { focusId, q }));
  const nodes = Array.from(pos.values()).sort((a, b) => cmp(a.id, b.id));
  return {
    width: maxX - minX + 2 * MARGIN, height: maxY - minY + 2 * MARGIN, mode: 'focus', nodes, groups: [],
    edges: buildEdges(incident, pos, true, () => false),
    shownNodes: nodes.length, totalNodes: input.nodes.length, shownEdges: incident.length, totalEdges,
  };
}
